import { Bot, KeyRound, Plus, Terminal, TriangleAlert, UserRound } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { ROLE_RANK, TIER_LABEL, TOKEN_MAX_DAYS } from '@orgus/protocol';
import type { ApiTokenInfo, AuthState, CreateTokenResponse, Role } from '@orgus/protocol';
import { useExt } from '../../lib/store.ts';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  Panel,
  RelativeTime,
  Segmented,
  Select,
  SkeletonRows,
  StatusDot,
  toast,
  useNow,
} from '@orgus/ui';
import type { Column } from '@orgus/ui';
import { Trash2 } from 'lucide-react';
import { authClient } from './client.ts';
import { CopyField } from './CopyField.tsx';
import { TIER_TONE } from '../ai/model.ts';
import { CUSTOM_ACCESS, ROLE_LABEL, ROLE_TONE, SCOPE_PRESETS, TOKEN_DAYS, customChoices, grantableRoles, roleOptions, scopeSummary } from './labels.ts';
import { useMe } from './useMe.ts';
import { useResource } from './useResource.ts';

const AGENT_NAME = /^[a-z0-9][a-z0-9._-]{0,39}$/i;

/**
 * Custom access: reads as one box, everyday changes by name, and elevated ones apart with
 * their tier and why they are risky. What the role does not reach stays listed, disabled.
 */
export function CustomAccess({
  role,
  kind,
  read,
  onRead,
  picked,
  onPicked,
  error,
}: {
  role: Role;
  kind: 'personal' | 'agent';
  read: boolean;
  onRead: (v: boolean) => void;
  picked: string[];
  onPicked: (v: string[]) => void;
  error: string | null;
}) {
  const { everyday, elevated } = customChoices(role, kind);
  const has = (name: string) => picked.includes(name);
  const toggle = (name: string, on: boolean) => onPicked(on ? [...picked, name] : picked.filter((n) => n !== name));
  const open = everyday.filter((c) => !c.disabled).map((c) => c.cap.name);
  const allOn = open.length > 0 && open.every(has);
  return (
    <div className="flex flex-col gap-4 rounded-card border border-border bg-sunken p-4" role="group" aria-label="Custom access">
      <Checkbox checked={read} onCheckedChange={onRead} label="Read everything" hint="State, sessions, missions, costs and the audit log." />
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <Checkbox
            checked={allOn}
            indeterminate={!allOn && open.some(has)}
            disabled={!open.length}
            onCheckedChange={(on) => onPicked(on ? [...new Set([...picked, ...open])] : picked.filter((n) => !open.includes(n)))}
            label="Everyday changes"
            hint={open.length ? 'Easy to undo. Agents reach these by default.' : everyday[0]?.disabled}
          />
          <Badge tone={TIER_TONE.operate} variant="outline">
            {TIER_LABEL.operate}
          </Badge>
        </div>
        {/* Down each column, in catalogue order: sessions and machines, then approvals, missions and the backlog. */}
        <div className="columns-2 gap-x-4 pl-6">
          {everyday.map(({ cap, disabled }) => (
            <Checkbox
              key={cap.name}
              title={cap.name}
              label={cap.title}
              checked={has(cap.name) && !disabled}
              disabled={!!disabled}
              onCheckedChange={(on) => toggle(cap.name, on)}
              wrapperClassName="mb-2 break-inside-avoid"
            />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-3 border-t border-border pt-4">
        <div className="flex flex-col">
          <span className="text-ui font-medium text-fg">Elevated</span>
          <span className="text-caption text-fg-subtle">{kind === 'agent' ? 'An agent reaches these only when they are ticked here.' : 'Changes that remove work or widen what runs unattended.'}</span>
        </div>
        <ul className="flex flex-col gap-3">
          {elevated.map(({ cap, disabled }) => (
            <li key={cap.name} className="flex items-start justify-between gap-3">
              <Checkbox
                title={cap.name}
                label={cap.title}
                hint={disabled ? `${cap.risk ?? ''} ${disabled}`.trim() : cap.risk}
                checked={has(cap.name) && !disabled}
                disabled={!!disabled}
                onCheckedChange={(on) => toggle(cap.name, on)}
              />
              <Badge tone={disabled ? 'neutral' : TIER_TONE[cap.tier]} variant="outline">
                {TIER_LABEL[cap.tier]}
              </Badge>
            </li>
          ))}
        </ul>
      </div>
      {error && (
        <p role="alert" className="text-caption text-failed">
          {error}
        </p>
      )}
    </div>
  );
}

function CreateTokenDialog({ open, onOpenChange, myRole, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; myRole: Role; onCreated: () => void }) {
  const [kind, setKind] = useState<'personal' | 'agent'>('agent');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [preset, setPreset] = useState('read');
  const [read, setRead] = useState(true);
  const [picked, setPicked] = useState<string[]>([]);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [days, setDays] = useState(String(TOKEN_MAX_DAYS));
  const [busy, setBusy] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateTokenResponse | null>(null);
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://127.0.0.1:4600';
  const custom = preset === CUSTOM_ACCESS.value;

  const reset = () => {
    setName('');
    setNameError(null);
    setRead(true);
    setPicked([]);
    setAccessError(null);
    setCreated(null);
    setBusy(false);
  };

  /** The scopes to send: a preset's, or what is ticked and the role still reaches. */
  const scopesToSend = (): string[] => {
    if (!custom) return SCOPE_PRESETS.find((p) => p.value === preset)?.scopes ?? [];
    const { everyday, elevated } = customChoices(role, kind);
    const open = new Set([...everyday, ...elevated].filter((c) => !c.disabled).map((c) => c.cap.name));
    return [...(read ? ['read'] : []), ...picked.filter((n) => open.has(n))];
  };
  const close = (v: boolean) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 200);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return setNameError('Give the token a name you will recognise later.');
    if (kind === 'agent' && !AGENT_NAME.test(n)) return setNameError('Use letters, digits, dot, dash or underscore, up to 40 characters. For example triage-bot.');
    if (n.length > 60) return setNameError('Keep the name to 60 characters.');
    setNameError(null);
    const scopes = scopesToSend();
    if (custom && !scopes.length) return setAccessError('Tick at least one thing the token may do.');
    setAccessError(null);
    setBusy(true);
    try {
      const res = await authClient.createToken({ name: n, kind, role, scopes, days: Number(days) });
      setCreated(res);
      onCreated();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    const t = created.info;
    return (
      <Dialog
        open={open}
        onOpenChange={close}
        title="Copy the token now"
        description="It is shown only this once."
        size="md"
        footer={
          <Button variant="primary" onClick={() => close(false)}>
            Done
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <CopyField value={created.token} label="API token" />
          <div className="flex items-center gap-3 rounded-md border border-waiting/30 bg-waiting-soft px-3 py-2.5 text-sm">
            <TriangleAlert className="size-4 shrink-0 text-waiting" aria-hidden />
            <p className="text-fg-muted">
              {t.kind === 'agent' ? `Everything done with it is audited as agent:${t.name}.` : `Everything done with it is audited as ${t.createdBy}.`} It expires <RelativeTime ts={t.expiresAt} />.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="flex items-center gap-1.5 text-sm text-fg-subtle">
              <Terminal className="size-3.5" aria-hidden /> Use it from a script (changes also need the X-Orgus header)
            </span>
            <CopyField multiline label="Example request" value={`curl -H "Authorization: Bearer ${created.token}" ${origin}/api/state`} />
            <span className="text-sm text-fg-subtle">Or save it for the orgus command on this machine:</span>
            <CopyField label="Login command" value={`orgus login --hub ${origin} --token ${created.token}`} />
          </div>
        </div>
      </Dialog>
    );
  }

  const presetHint = custom ? CUSTOM_ACCESS.hint : SCOPE_PRESETS.find((p) => p.value === preset)?.hint;
  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title="New API token"
      description="For scripts, CI and AI agents that use this hub's API. Tokens expire within 30 days."
      size={custom ? 'lg' : 'md'}
      footer={
        <>
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="new-token" loading={busy}>
            Create token
          </Button>
        </>
      }
    >
      <form id="new-token" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Segmented
          label="Who uses the token"
          value={kind}
          onChange={(v) => {
            setKind(v);
            setNameError(null);
          }}
          options={[
            { value: 'agent', label: 'AI agent', icon: <Bot /> },
            { value: 'personal', label: 'Personal', icon: <UserRound /> },
          ]}
        />
        <Input
          label={kind === 'agent' ? 'Agent name' : 'Name'}
          hint={kind === 'agent' ? `Shows in the audit log as agent:${name.trim() || 'name'}.` : 'Where you will use it, for example ci-nightly. Actions are audited as you.'}
          placeholder={kind === 'agent' ? 'triage-bot' : 'ci-nightly'}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={nameError ?? undefined}
          autoComplete="off"
          spellCheck={false}
          autoFocus
        />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Role" hint="At most your own role." value={role} onValueChange={(v) => setRole(v as Role)} options={roleOptions(grantableRoles(myRole))} />
          <Select label="Expires after" value={days} onValueChange={setDays} options={TOKEN_DAYS} />
        </div>
        <Select
          label="Access"
          hint={presetHint}
          value={preset}
          onValueChange={(v) => {
            setPreset(v);
            setAccessError(null);
          }}
          options={[...SCOPE_PRESETS, CUSTOM_ACCESS].map((p) => ({ value: p.value, label: p.label }))}
        />
        {custom && (
          <CustomAccess
            role={role}
            kind={kind}
            read={read}
            onRead={setRead}
            picked={picked}
            onPicked={(v) => {
              setPicked(v);
              setAccessError(null);
            }}
            error={accessError}
          />
        )}
      </form>
    </Dialog>
  );
}

function ExpiryCell({ t }: { t: ApiTokenInfo }) {
  const now = useNow(60_000);
  if (t.expiresAt <= now) {
    return (
      <span className="flex items-center gap-1.5 text-sm text-failed">
        <StatusDot tone="failed" label="" /> Expired
      </span>
    );
  }
  const soon = t.expiresAt - now < 3 * 24 * 60 * 60_000;
  return (
    <span className={soon ? 'flex items-center gap-1.5 text-sm text-waiting' : 'text-sm text-fg-muted'}>
      {soon && <StatusDot tone="waiting" label="" />}
      <RelativeTime ts={t.expiresAt} />
    </span>
  );
}

export function TokensView() {
  const me = useMe((s) => s.me);
  const myRole: Role = me?.actor.role ?? 'viewer';
  // Re-read when the hub says auth changed (state.ext.auth.rev over SSE), instead of polling.
  const rev = useExt<AuthState>('auth')?.rev;
  const { data, error, loading, refresh } = useResource(authClient.tokens, undefined, rev);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiTokenInfo | null>(null);
  const canManage = data?.canManage ?? false;
  const mine = (t: ApiTokenInfo) => t.createdBy === me?.actor.id;
  const usingThis = (t: ApiTokenInfo) => me?.token?.id === t.id;

  const columns: Column<ApiTokenInfo>[] = [
    {
      key: 'name',
      header: 'Token',
      grow: true,
      text: (t) => `${t.name} ${t.createdBy}`,
      sortValue: (t) => t.name.toLowerCase(),
      cell: (t) => (
        <span className="flex min-w-0 items-center gap-2.5">
          {t.kind === 'agent' ? <Bot className="size-4 shrink-0 text-fg-subtle" aria-label="AI agent token" /> : <UserRound className="size-4 shrink-0 text-fg-subtle" aria-label="Personal token" />}
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <span className="truncate font-medium text-fg">{t.kind === 'agent' ? `agent:${t.name}` : t.name}</span>
              {usingThis(t) && <Badge tone="neutral" variant="outline">This session</Badge>}
            </span>
            <span className="block truncate text-sm text-fg-subtle">
              {scopeSummary(t)}, made by {t.createdBy}
            </span>
            {/* Below 768 px Role and Expires fold into one meta line, so the name keeps the width. */}
            <span className="flex items-center gap-1 text-caption text-fg-subtle md:hidden">
              {ROLE_LABEL[t.role]}
              <span aria-hidden>·</span>
              <ExpiryCell t={t} />
            </span>
          </span>
        </span>
      ),
    },
    { key: 'role', header: 'Role', type: 'badge', hideBelow: 'md', sortValue: (t) => ROLE_RANK[t.role], cell: (t) => <Badge tone={ROLE_TONE[t.role]}>{ROLE_LABEL[t.role]}</Badge> },
    { key: 'used', header: 'Last used', type: 'date', hideBelow: 'md', sortValue: (t) => t.lastUsedAt ?? 0, cell: (t) => <RelativeTime ts={t.lastUsedAt} fallback="Never" /> },
    { key: 'created', header: 'Created', type: 'date', hideBelow: 'lg', sortValue: (t) => t.createdAt, cell: (t) => <RelativeTime ts={t.createdAt} /> },
    { key: 'expires', header: 'Expires', type: 'date', hideBelow: 'md', sortValue: (t) => t.expiresAt, cell: (t) => <ExpiryCell t={t} /> },
  ];

  if (loading && !data) {
    return (
      <div role="status" aria-label="Loading tokens">
        <SkeletonRows rows={4} />
      </div>
    );
  }
  if (!data) return <ErrorState error={error} onRetry={refresh} />;

  return (
    <div className="flex flex-col gap-4">
      {error != null && <ErrorState variant="inline" error={error} onRetry={refresh} />}
      <Panel
        title={canManage ? 'All tokens' : 'Your tokens'}
        sub={`${data.tokens.length}`}
        icon={<KeyRound />}
        flush
        actions={
          canManage ? (
            <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              New token
            </Button>
          ) : undefined
        }
      >
        <DataTable
          frameless
          label="API tokens"
          rows={data.tokens}
          rowKey={(t) => t.id}
          columns={columns}
          pageSize={false}
          searchable={data.tokens.length > 8 ? { placeholder: 'Search tokens' } : undefined}
          initialSort={{ key: 'created', dir: 'desc' }}
          empty={
            <EmptyState
              icon={<KeyRound />}
              title="No API tokens yet"
              description={
                canManage
                  ? 'Tokens let scripts, CI and AI agents use this hub. orgus login makes a personal one for your terminal.'
                  : 'Run orgus login in a terminal to get a personal token. Only admins create other tokens.'
              }
              action={
                canManage ? (
                  <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                    New token
                  </Button>
                ) : undefined
              }
            />
          }
          rowActions={(t) => (canManage || mine(t) ? <IconButton label={`Revoke ${t.name}`} icon={<Trash2 />} size="sm" onClick={() => setRevoking(t)} /> : null)}
        />
      </Panel>
      <p className="text-sm text-fg-subtle">
        Send a token as <span className="font-mono">Authorization: Bearer ast_...</span>. Agent tokens act as <span className="font-mono">agent:name</span> in the audit
        log. A token never has more power than the person who made it has now.
      </p>
      <CreateTokenDialog open={creating} onOpenChange={setCreating} myRole={myRole} onCreated={() => void refresh()} />
      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(v) => !v && setRevoking(null)}
        title={revoking ? `Revoke ${revoking.kind === 'agent' ? `agent:${revoking.name}` : revoking.name}?` : ''}
        description={
          revoking && usingThis(revoking)
            ? 'This is the token this page is using. Requests with it stop working at once, including this one.'
            : 'Anything using it stops working at once. This cannot be undone; make a new token if it is still needed.'
        }
        confirmLabel="Revoke token"
        onConfirm={async () => {
          if (!revoking) return;
          await authClient.revokeToken(revoking.id);
          toast.success(`${revoking.name} was revoked`);
          setRevoking(null);
          void refresh();
        }}
      />
    </div>
  );
}
